/**
 * NodeReferenceNode - 自定義 Lexical 節點用於引用其他節點
 */

import React from 'react';
import { DecoratorNode } from 'lexical';
import { convertIdToAlphanumeric } from '../../../utils/idConverter';

export class NodeReferenceNode extends DecoratorNode {
  __nodeId;
  __nodeTitle;

  static getType() {
    return 'node-reference';
  }

  static clone(node) {
    return new NodeReferenceNode(node.__nodeId, node.__nodeTitle, node.__key);
  }

  constructor(nodeId, nodeTitle = '', key) {
    super(key);
    this.__nodeId = nodeId;
    this.__nodeTitle = nodeTitle;
  }

  createDOM(config) {
    // 創建容器元素（用於 decorate() 渲染）
    const span = document.createElement('span');
    span.className = 'node-reference-decorator';
    
    // 保存數據到 data 屬性，以便序列化和反序列化
    span.setAttribute('data-node-id', this.__nodeId);
    span.setAttribute('data-node-title', this.__nodeTitle);

    return span;
  }

  updateDOM() {
    return false;
  }

  exportDOM() {
    // 用於 HTML 導出的完整元素
    const span = document.createElement('span');
    span.className = 'node-reference';
    
    // 保存數據到 data 屬性
    span.setAttribute('data-node-id', this.__nodeId);
    span.setAttribute('data-node-title', this.__nodeTitle);
    
    span.style.cssText = `
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 2px 8px;
      margin: 0 2px;
      background-color: #eff6ff;
      border: 1px solid #3b82f6;
      border-radius: 4px;
      color: #2563eb;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s;
      user-select: none;
    `;
    
    // 創建 ID 標籤
    const idBadge = document.createElement('span');
    idBadge.style.cssText = `
      font-size: 0.75rem;
      font-weight: bold;
      background-color: #3b82f6;
      color: white;
      padding: 1px 4px;
      border-radius: 3px;
      margin-right: 4px;
    `;
    idBadge.textContent = convertIdToAlphanumeric(this.__nodeId);
    
    // 創建標題文字
    const titleSpan = document.createElement('span');
    titleSpan.textContent = this.__nodeTitle;
    
    // 組合元素
    span.appendChild(idBadge);
    span.appendChild(titleSpan);

    return { element: span };
  }

  decorate() {
    return (
      <span
        className="node-reference"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: '2px 8px',
          margin: '0 2px',
          backgroundColor: '#eff6ff',
          border: '1px solid #3b82f6',
          borderRadius: '4px',
          color: '#2563eb',
          fontWeight: 500,
          cursor: 'pointer',
          transition: 'all 0.2s',
          userSelect: 'none'
        }}
        onClick={() => {
          // 觸發自定義事件以打開預覽對話框
          const event = new CustomEvent('node-reference-click', {
            detail: { nodeId: this.__nodeId, nodeTitle: this.__nodeTitle }
          });
          document.dispatchEvent(event);
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#dbeafe';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#eff6ff';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        <span style={{ 
          fontSize: '0.75rem',
          fontWeight: 'bold',
          backgroundColor: '#3b82f6',
          color: 'white',
          padding: '1px 4px',
          borderRadius: '3px',
          marginRight: '4px'
        }}>
          {convertIdToAlphanumeric(this.__nodeId)}
        </span>
        <span>{this.__nodeTitle}</span>
      </span>
    );
  }

  getNodeId() {
    return this.__nodeId;
  }

  getNodeTitle() {
    return this.__nodeTitle;
  }

  isInline() {
    return true;
  }

  static importJSON(serializedNode) {
    const node = $createNodeReferenceNode(
      serializedNode.nodeId,
      serializedNode.nodeTitle
    );
    return node;
  }

  exportJSON() {
    return {
      ...super.exportJSON(),
      nodeId: this.__nodeId,
      nodeTitle: this.__nodeTitle
    };
  }

  static importDOM() {
    return {
      span: (domNode) => {
        // 只處理帶有 node-reference class 的 span
        if (!domNode.hasAttribute('class') || !domNode.className.includes('node-reference')) {
          return null;
        }
        
        return {
          conversion: (element) => {
            const nodeId = element.getAttribute('data-node-id');
            const nodeTitle = element.getAttribute('data-node-title');
            
            if (nodeId) {
              const node = $createNodeReferenceNode(
                parseInt(nodeId, 10),
                nodeTitle || ''
              );
              return { node };
            }
            
            return null;
          },
          priority: 1
        };
      }
    };
  }
}

export function $createNodeReferenceNode(nodeId, nodeTitle) {
  return new NodeReferenceNode(nodeId, nodeTitle);
}

export function $isNodeReferenceNode(node) {
  return node instanceof NodeReferenceNode;
}
